import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { safeNext } from '../auth/guards'
import { Icon } from '../components/Icon'
import { Field } from '../components/ui'
import { canAccessPath, homePath } from '../lib/permissions'

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return 'Demasiados intentos. Esperar un minuto e intentar de nuevo.'
    if (error.status === 0) return 'No se puede conectar con el servidor. Revisar la conexión e intentar de nuevo.'
    return error.message
  }
  return 'No se pudo iniciar sesión. Intentar de nuevo.'
}

export function LoginPage() {
  const { status, user, login } = useAuth()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const next = safeNext(params.get('next'))
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({})
  const [busy, setBusy] = useState(false)

  if (status === 'authenticated' && user) {
    return <Navigate to={canAccessPath(user.role, next) ? next : homePath(user.role)} replace />
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const missing = { username: username.trim() ? undefined : 'Escribir el nombre de usuario.', password: password ? undefined : 'Escribir la contraseña.' }
    setFieldErrors(missing)
    if (missing.username || missing.password) return
    setBusy(true)
    setError(null)
    try {
      const signedIn = await login(username.trim(), password)
      navigate(canAccessPath(signedIn.role, next) ? next : homePath(signedIn.role), { replace: true })
    } catch (e) {
      setError(messageFor(e))
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login">
      <div className="login__card">
        <img className="login__logo" src="/brand/drivetech-logo.png" width="283" height="207" alt="Drive Tech - Drive and Energy Technologies" />
        <div className="login__brand">
          <div>
            <h1>Iniciar sesión</h1>
            <p className="muted">Plataforma de inspección de motorreductores</p>
          </div>
        </div>
        <form onSubmit={submit} noValidate className="form-stack">
          <Field label="Nombre de usuario" htmlFor="username" error={fieldErrors.username}>
            <input id="username" name="username" autoComplete="username" autoCapitalize="none" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} aria-invalid={Boolean(fieldErrors.username)} />
          </Field>
          <Field label="Contraseña" htmlFor="password" error={fieldErrors.password}>
            <input id="password" name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={Boolean(fieldErrors.password)} />
          </Field>
          {error ? <p role="alert" className="form-error"><Icon name="triangle-alert" size={16} /> {error}</p> : null}
          <button type="submit" className="btn btn--primary btn--lg" disabled={busy}>{busy ? 'Iniciando sesión…' : 'Iniciar sesión'}</button>
        </form>
        <p className="faint login__foot">Las sesiones usan cookies seguras. No se guarda nada en el almacenamiento del navegador.</p>
      </div>
    </main>
  )
}
